import { STORAGE_KEY } from '../contexts/StorageContext';
import { storePriceLabel } from '../constants/stores';
import type {
  DecidedBatchItem, DecidedBatchResponse, DecidedBatchRow,
  DecidedPriceRequestItem, DecidedPriceResult, DecisionListItem, DecisionMarketItems, DecisionMarketSummary, NaverSaveStatus,
} from '../types/decidedPrice';

// ─── "판매가 결정" 화면의 순수 로직(감사 card-controltower#341) ─────────────────────────────────────────────
//
// 화면 상태와 떨어진 것만 모았다 — 응답 정규화 · 저장 결과 판정 · 결과 불명 요청 보관 · 일괄 대상 계산 · 즐겨찾기.

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

// ─── 응답 정규화 ──────────────────────────────────────────────────────────────

function toSummary(raw: unknown): DecisionMarketSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const s: DecisionMarketSummary = {
    min: num(r.min), avg: num(r.avg), median: num(r.median), max: num(r.max), shopCount: num(r.shopCount),
  };
  if ('collectedAt' in r) s.collectedAt = str(r.collectedAt);
  return s;
}

/**
 * 판매처 목록 응답 → 계약 모양. 계약은 `{ collectedAt, items: [{shop, title, price, url, imageUrl}], summary }` 이고, 지금
 * 운영 서버의 같은 경로는 하락 검토용 옛 모양(`items: [{mallName, title, price, link, image}], minPrice · maxPrice · avgPrice`)이다 —
 * 백엔드(#340)가 바꾸기 전 · 뒤 어느 쪽이 와도 읽는다.
 */
export function normalizeMarketItems(raw: unknown): DecisionMarketItems {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const list = Array.isArray(r.items) ? r.items : [];
  const items = list.map((it) => {
    const i = (it && typeof it === 'object' ? it : {}) as Record<string, unknown>;
    return {
      shop:     str(i.shop) ?? str(i.mallName),
      title:    str(i.title),
      price:    num(i.price),
      url:      str(i.url) ?? str(i.link),
      imageUrl: str(i.imageUrl) ?? str(i.image),
    };
  });
  let summary = toSummary(r.summary);
  if (!summary && ('minPrice' in r || 'avgPrice' in r || 'maxPrice' in r)) {
    summary = {
      min: num(r.minPrice), avg: num(r.avgPrice), median: null, max: num(r.maxPrice),
      shopCount: items.length || null,
    };
  }
  return { collectedAt: str(r.collectedAt), items, summary };
}

const NAVER_STATUSES: NaverSaveStatus[] = ['SUCCESS', 'FAILED', 'UNKNOWN'];

// ─── PriceHub 경고(감사 card-controltower#362) ────────────────────────────────

export const HUB_WARNING_LABEL: Record<string, string> = {
  LOCKED: 'PriceHub 고정',
  PENDING_REQUEST: '요청 대기',
  PENDING_CONFIRM: '확정 대기',
};

export const HUB_WARNING_HELP: Record<string, string> = {
  LOCKED: 'PriceHub 에서 이 카드의 판매가가 고정돼 있습니다 — 결정해도 PriceHub 값은 관리자가 고정을 풀기 전까지 그대로일 수 있습니다',
  PENDING_REQUEST: 'PriceHub 에 이 카드의 매장 가격 요청이 대기 중입니다 — 결정하면 그 요청과 겹칠 수 있습니다',
  PENDING_CONFIRM: 'PriceHub 에 확정 대기 알림이 있습니다(이미 결정해 확정을 기다리는 카드 포함) — PriceHub 관리자가 확정 · 무시합니다',
};

export const hubWarningLabel = (w: string): string => HUB_WARNING_LABEL[w] ?? w;
export const hubWarningHelp = (w: string): string => HUB_WARNING_HELP[w] ?? `PriceHub 경고(${w})`;

// ─── <매장> 가격 칸(감사 card-controltower#455, 세 화면 재설계 F5) ──────────────────────────────────────────

/** hubPriceSource 가 매장 값(STORE)인가 — 아니면 PriceHub 공통가 · 매장 미인식. */
export const isStoreHubSource = (source: string | null | undefined): boolean => !!source && /store|매장/i.test(source);

/**
 * hubPriceSource 의 종류 — 매장 값 · PriceHub 공통가 · 매장 미인식 · 출처 없음(코덱 화면 점검 PR #74 중간).
 * 'none' 은 값(hubPrice)은 있는데 출처가 비었거나 모르는 글자인 경우 — 공통가로 뭉뚱그리지 않는다.
 */
export type HubSourceKind = 'store' | 'hub' | 'unresolved' | 'none';
export function hubSourceKind(source: string | null | undefined): HubSourceKind {
  if (isStoreHubSource(source)) return 'store';
  if (!source || !source.trim()) return 'none';
  if (/unresolved|미인식/i.test(source)) return 'unresolved';
  if (/hub|공통|common/i.test(source)) return 'hub';
  return 'none';
}

/** 표 칸에 늘 보이는 짧은 출처(매장 값이면 빈 글자 — 칸 머리가 이미 '<매장> 가격'). 툴팁에만 숨기지 않는다. */
export const HUB_SOURCE_SHORT: Record<HubSourceKind, string> = { store: '', hub: '공통가', unresolved: '미인식', none: '출처 없음' };

/** hubPriceSource(STORE · HUB · UNRESOLVED) → 화면 용어(감사 #328): 매장 값이면 "<매장> 가격", 그 밖은 "PriceHub 가격(…)". */
export function hubPriceLabel(source: string | null | undefined, store: string | null): string {
  const kind = hubSourceKind(source);
  if (kind === 'store') return storePriceLabel(store);
  if (kind === 'unresolved') return 'PriceHub 가격(매장 미인식)';
  if (kind === 'none') return 'PriceHub 가격(출처 없음)';
  return 'PriceHub 가격';
}

/**
 * <매장> 가격이 네이버 가격보다 얼마나 다른가 — 표의 차이 표시. 둘 중 하나라도 없거나 네이버 가격이 0 이하면 null.
 * pct 는 네이버 가격 기준 정수 %(반올림). 1% 미만이라도 값이 다르면 0 이 아니라 ±1 로(같음과 구분).
 */
export function storePriceDiff(hub: number | null | undefined, naver: number | null | undefined):
  { diff: number; pct: number; text: string } | null {
  if (hub == null || naver == null || naver <= 0) return null;
  const diff = hub - naver;
  if (diff === 0) return { diff, pct: 0, text: '같음' };
  const raw = Math.round((diff / naver) * 100);
  const pct = raw === 0 ? Math.sign(diff) : raw;
  return { diff, pct, text: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%` };
}

/**
 * 저장 결과의 PriceHub 보고가 확정 대기로 들어갔는가 — reported=true 라도 "이미 같은 값 — 확정할 것 없음" 이면 대기가 아니다
 * (백엔드 DecidedPriceService.applyReportResult 의 두 문구).
 */
export const hubReportPending = (hub: { reported: boolean; message: string | null } | null | undefined): boolean =>
  !!hub && hub.reported && !/확정할 것 없음/.test(hub.message ?? '');

/** 응답의 hubWarnings → 대문자 문자열 배열(중복 없음). 없거나 모양이 다르면 []. */
export function normalizeHubWarnings(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return Array.from(new Set(raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '').map((v) => v.trim().toUpperCase())));
}

/** 두 경고 목록을 합친다 — 재전송 응답의 [] 로 목록의 경고를 지우지 않게(합집합). */
export const mergeHubWarnings = (a: string[] | undefined, b: string[] | undefined): string[] =>
  Array.from(new Set([...(a ?? []), ...(b ?? [])]));

/**
 * 저장 응답 한 건 → 결과. 모양이 계약과 다르면(naver.status 가 없거나 모르는 값) '결과 불명' 으로 본다 — 네이버에 반영됐는지
 * 모르는 채 새 요청으로 다시 보내지 않게.
 */
export function normalizeSaveResult(raw: unknown, fallback: { cardId: number; price: number }): DecidedPriceResult {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const naver = (r.naver && typeof r.naver === 'object' ? r.naver : {}) as Record<string, unknown>;
  const statusRaw = typeof naver.status === 'string' ? naver.status.toUpperCase() : '';
  const status = (NAVER_STATUSES as string[]).includes(statusRaw) ? statusRaw as NaverSaveStatus : 'UNKNOWN';
  const hubRaw = r.hub && typeof r.hub === 'object' ? r.hub as Record<string, unknown> : null;
  const unknownStatus = statusRaw !== '' && !(NAVER_STATUSES as string[]).includes(statusRaw);
  // 결정 시각(#501) — 키가 있을 때만 싣는다(null = 서버가 결정을 남기지 않음, 키 없음 = 옛 서버라 모름).
  const decided: Pick<DecidedPriceResult, 'decidedAt'> = 'decidedAt' in r ? { decidedAt: str(r.decidedAt) } : {};
  return {
    ...decided,
    cardId: num(r.cardId) ?? fallback.cardId,
    price:  num(r.price) ?? fallback.price,
    naver:  {
      status,
      message: str(naver.message)
        ?? (unknownStatus ? `알 수 없는 응답(${statusRaw})` : null)
        ?? str(r.message) ?? str(r.error),
    },
    hub: hubRaw ? { reported: hubRaw.reported === true, message: str(hubRaw.message) } : null,
    hubWarnings: normalizeHubWarnings(r.hubWarnings),
  };
}

/**
 * 일괄 저장 결과(묶음 잡의 decisions — 요청 순서) → cardId 별 결과. 배열 · `{ decisions }` · `{ results }` · `{ items }` 어느 모양이든 읽고, 보낸 카드 중 응답에 없는 것은
 * 결과 불명으로 채운다.
 */
export function normalizeBulkResults(raw: unknown, sent: DecidedPriceRequestItem[]): Map<number, DecidedPriceResult> {
  const r = raw as { decisions?: unknown; results?: unknown; items?: unknown } | unknown[] | null;
  const list: unknown[] = Array.isArray(r) ? r
    : Array.isArray((r as { decisions?: unknown })?.decisions) ? (r as { decisions: unknown[] }).decisions
      : Array.isArray((r as { results?: unknown })?.results) ? (r as { results: unknown[] }).results
        : Array.isArray((r as { items?: unknown })?.items) ? (r as { items: unknown[] }).items
          : [];
  const sentById = new Map(sent.map((s) => [s.cardId, s]));
  const out = new Map<number, DecidedPriceResult>();
  for (const it of list) {
    const id = num((it as { cardId?: unknown })?.cardId);
    if (id == null) continue;
    const s = sentById.get(id);
    if (!s) continue; // 보내지 않은 카드의 결과는 버린다
    out.set(id, normalizeSaveResult(it, s));
  }
  for (const s of sent) {
    if (!out.has(s.cardId)) {
      out.set(s.cardId, {
        cardId: s.cardId, price: s.price, naver: { status: 'UNKNOWN', message: '응답에 이 카드 결과가 없습니다' }, hub: null,
      });
    }
  }
  return out;
}

/**
 * batch 응답(백엔드 PR #97) → 보낸 항목 순서의 줄. 보낸 카드 중 응답에 없는 것은 ok=false · result 없음 · error '응답에 이 카드 결과가
 * 없습니다'(부른 쪽이 결과 불명으로 다룬다 — 서버가 썼는지 모른다). 보내지 않은 카드 · requestId 가 다른 줄은 버린다.
 */
export function normalizeBatchResponse(raw: unknown, sent: DecidedBatchItem[]): DecidedBatchResponse {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const list = Array.isArray(r.results) ? r.results : [];
  const byId = new Map<number, DecidedBatchRow>();
  const sentById = new Map(sent.map((s) => [s.cardId, s]));
  for (const it of list) {
    const o = (it && typeof it === 'object' ? it : {}) as Record<string, unknown>;
    const id = num(o.cardId);
    const s = id != null ? sentById.get(id) : undefined;
    if (!s) continue;
    if (typeof o.requestId === 'string' && o.requestId !== s.requestId) continue;
    const ok = o.ok === true;
    byId.set(s.cardId, {
      cardId: s.cardId,
      requestId: s.requestId,
      ok,
      error: str(o.error),
      result: o.result && typeof o.result === 'object' ? normalizeSaveResult(o.result, s) : null,
    });
  }
  const results = sent.map((s) => byId.get(s.cardId) ?? {
    cardId: s.cardId, requestId: s.requestId, ok: false, error: MISSING_ROW, result: null,
  });
  const count = (k: string, fallback: number) => num(r[k]) ?? fallback;
  const okRows = results.filter((x) => x.ok);
  return {
    results,
    okCount: count('okCount', okRows.length),
    failCount: count('failCount', results.length - okRows.length),
    naverFailCount: count('naverFailCount', okRows.filter((x) => x.result?.naver.status === 'FAILED').length),
    naverUnknownCount: count('naverUnknownCount', okRows.filter((x) => x.result?.naver.status === 'UNKNOWN').length),
  };
}

/** batch 응답에 그 카드 줄이 없을 때의 사유 — 서버가 썼는지 모르니 결과 불명. */
export const MISSING_ROW = '응답에 이 카드 결과가 없습니다';

// ─── 저장 실패 판정 ───────────────────────────────────────────────────────────

export interface SaveErrorOutcome {
  /**
   * failed = 서버가 받지 않았다(새 요청으로 다시 보내도 된다) · unknown = 반영됐는지 모른다(같은 requestId 로만) ·
   * busy = 같은 requestId 를 서버가 아직 처리 중(409) — 요청 번호는 그대로 두고 잠시 다시 보내지 않는다.
   */
  kind: 'failed' | 'unknown' | 'busy';
  message: string;
}

/** 백엔드(#340) 의 '같은 요청 처리 중' 문구(IN_FLIGHT_MESSAGE) — 구조화된 칸이 없어 문구로 알아본다. */
const IN_FLIGHT_PATTERN = /처리하는 중|처리 중/;

/**
 * 같은 requestId 를 서버가 아직 처리 중이라는 문구인가 — 단건은 409, 묶음 decisions 는 naver.status=FAILED + 이 문구로 온다
 * (백엔드 decideOne). 실패가 아니라 처리 중으로 읽어 요청 번호를 지우지 않는다(Opus 리뷰 중간 2).
 */
export const isInFlightMessage = (msg: string | null | undefined): boolean => !!msg && IN_FLIGHT_PATTERN.test(msg);

/**
 * 저장 요청이 오류로 끝났을 때 — 4xx 는 서버가 거절(반영 안 됨), 응답이 없거나(끊김 · 시간 초과) 5xx 는 서버가 네이버에
 * 보냈는지 모르니 '결과 불명'. 502 + resultUnknown(백엔드 ApiExceptionHandler 의 PriceHub 응답 타임아웃)도 결과 불명.
 * 409 는 '같은 요청 처리 중' 이거나, 결과 불명 요청을 같은 번호로 다시 보낸 경우(retry)면 busy — 요청 번호를 지우지 않는다(서버가
 * 그 요청을 처리했을 수 있다). 처음 보낸 요청의 409(가격 조회 등 잡이 도는 중)는 처리 안 됨이라 failed.
 */
export function saveErrorOutcome(e: unknown, opts: { retry?: boolean } = {}): SaveErrorOutcome {
  const err = e as { response?: { status?: number; data?: { message?: unknown; resultUnknown?: unknown } }; code?: string };
  const status = err?.response?.status;
  const serverMsg = typeof err?.response?.data?.message === 'string' ? err.response.data.message : null;
  if (status == null) {
    const timeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';
    return { kind: 'unknown', message: timeout ? '응답 시간 초과 — 결과 불명' : '응답을 받지 못했습니다 — 결과 불명' };
  }
  if (status >= 500 || err?.response?.data?.resultUnknown === true) {
    return { kind: 'unknown', message: serverMsg ? `${serverMsg} — 결과 불명` : `서버 오류(${status}) — 결과 불명` };
  }
  if (status === 409 && (isInFlightMessage(serverMsg) || opts.retry)) {
    return { kind: 'busy', message: serverMsg ? `이미 처리 중 — ${serverMsg}` : '이미 처리 중 — 잠시 뒤 다시 시도하세요' };
  }
  // 결과 불명 요청을 같은 번호로 다시 보냈는데 401 · 403 이면 서버가 첫 요청 결과를 알려 주지 않은 것 — '확인 못 함' 이라 번호를 지우지
  // 않는다(새 번호로 다시 보내면 두 번 반영될 수 있다, 코덱 리뷰 높음 1). 처음 보낸 요청의 401 · 403 은 처리 안 됨이라 실패.
  if ((status === 401 || status === 403) && opts.retry) {
    return {
      kind: 'unknown',
      message: status === 401 ? '세션 만료로 결과를 확인하지 못했습니다 — 다시 로그인한 뒤 다시 시도' : '권한이 없어 결과를 확인하지 못했습니다',
    };
  }
  if (status === 401) return { kind: 'failed', message: '세션이 만료되었습니다(다시 로그인해 주세요)' };
  if (status === 403) return { kind: 'failed', message: '이 작업을 할 권한이 없습니다' };
  return { kind: 'failed', message: serverMsg ?? `저장하지 못했습니다(${status})` };
}

// ─── 가격 ─────────────────────────────────────────────────────────────────────

/** 백엔드 결정가 상한(1억, #340). */
export const MAX_SALE_PRICE = 100_000_000;

/** 입력칸 글자 → 원(숫자만). 비었으면 null. */
export function parsePriceInput(raw: string | undefined | null): number | null {
  if (raw == null) return null;
  const d = raw.replace(/[^0-9]/g, '');
  if (!d) return null;
  const n = parseInt(d, 10);
  return Number.isFinite(n) ? n : null;
}

/** 판매가 규칙(네이버 10원 단위 · 백엔드 상한 1억) — 어기면 안내 문구, 맞으면 null. */
export function validateSalePrice(price: number | null): string | null {
  if (price == null) return '가격을 입력하세요';
  if (!Number.isInteger(price) || price < 10) return '판매가는 10원 이상이어야 합니다';
  if (price % 10 !== 0) return '판매가는 10원 단위로 입력하세요';
  if (price > MAX_SALE_PRICE) return '판매가가 너무 큽니다(최대 100,000,000원)';
  return null;
}

/** 시장 요약값(평균 등 소수) → 10원 단위로 반올림. */
export const roundTo10 = (n: number): number => Math.round(n / 10) * 10;

// ─── 구분(일반 · 특일 · 미러) ─────────────────────────────────────────────────

export type DecisionKind = 'normal' | 'special' | 'mirror';

export const DECISION_KIND_LABEL: Record<DecisionKind, string> = { normal: '일반', special: '특일', mirror: '미러' };

export const isMirror = (m: DecisionListItem['mirror']): boolean =>
  typeof m === 'string' ? m.trim() !== '' && m.toLowerCase() !== 'false' : m === true;

/**
 * 구분 — 미러는 응답의 mirror, 특일은 계약에 칸이 없어 상품명의 '특일' · '특별 일러스트' 로 본다(PriceHub 는 is_teukil 칸이
 * 있다 — 백엔드가 내려 주면 그걸로 바꾼다).
 */
export function decisionKindOf(item: Pick<DecisionListItem, 'mirror' | 'productName'>): DecisionKind {
  if (isMirror(item.mirror)) return 'mirror';
  if (/특일|특별\s*일러/.test(item.productName ?? '')) return 'special';
  return 'normal';
}

// ─── 링크 ─────────────────────────────────────────────────────────────────────

/** http(s) 주소만 연다 — 응답의 url 에 javascript: 같은 것이 와도 링크로 쓰지 않는다. */
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
}

export const naverShoppingUrl = (query: string): string =>
  `https://search.shopping.naver.com/search/all?query=${encodeURIComponent(query.trim())}`;

// ─── 매장 · 요청 번호 ─────────────────────────────────────────────────────────

/**
 * 다음 요청이 실제로 향할 매장 — http 인터셉터가 요청마다 localStorage 의 superStore 를 X-Store-Code 로 싣는다(SUPER). 다른 탭에서
 * 매장을 바꾸면 이 탭 화면은 앞 매장 카드인데 요청은 새 매장으로 나간다(감사 card-controltower#310 과 같은 대비).
 */
export const currentRequestStore = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY.store) ?? localStorage.getItem(STORAGE_KEY.superStore);
  } catch {
    return null;
  }
};

/**
 * 지금 요청이 나갈 세션 — 계정(role · store · username) · SUPER 가 고른 매장 · 로그인 시각. 로그아웃 · 다른 계정 · 같은 계정 다시 로그인이면
 * 바뀐다(감사 card-controltower#855 — 기다리던 저장을 새 쿠키로 다시 보내지 않게). 매장만 비교하면 같은 매장의 다른 계정을 못 가린다.
 */
export const currentSessionKey = (): string => {
  try {
    return [STORAGE_KEY.role, STORAGE_KEY.store, STORAGE_KEY.superStore, STORAGE_KEY.username, STORAGE_KEY.loginAt]
      .map((k) => localStorage.getItem(k) ?? '').join('|');
  } catch {
    return '';
  }
};

export const sameStore = (a: string | null, b: string | null): boolean =>
  a != null && b != null && a.toUpperCase() === b.toUpperCase();

/** 요청 번호(멱등 키) — crypto.randomUUID 가 없으면(옛 브라우저 · http) 시각 + 난수. */
export function newRequestId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    // 아래로
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── 결과 불명 요청 보관(매장별 localStorage) ──────────────────────────────────
//
// 보내기 직전에 { 가격, requestId } 를 적고(먼저 적기), 네이버 결과가 확실해지면(성공 · 실패) 지운다. 응답을 못 받았거나 결과 불명이면
// 남아 있어서 — 새로고침 · 창을 닫았다 열어도 — 그 카드는 같은 가격 · 같은 requestId 로만 다시 보낸다.

export interface PendingSave {
  price:     number;
  requestId: string;
  /** 처음 보낸 시각(ISO). */
  at:        string;
}

const PENDING_PREFIX = 'decide:unknown';
const FAVORITE_PREFIX = 'decide:fav';

export const pendingKey = (store: string | null): string => `${PENDING_PREFIX}:${(store ?? 'none').toUpperCase()}`;
export const favoriteKey = (store: string | null): string => `${FAVORITE_PREFIX}:${(store ?? 'none').toUpperCase()}`;

/** 카드 하나의 보관 키 — 카드마다 따로 적어 두 탭이 동시에 저장해도 서로의 요청 번호를 덮지 않는다(코덱 리뷰 중간 5). */
export const pendingCardKey = (store: string | null, cardId: number): string => `${pendingKey(store)}:${cardId}`;

// localStorage 쓰기가 실패한 키의 값(null = 지움) — 저장소가 가득 차도 이 탭에서는 잠금이 남는다(코덱 리뷰 높음 2).
const memoryPending = new Map<string, PendingSave | null>();
// 쓰기가 한 번이라도 실패한 매장(보관 키 머리) — 화면이 경고를 띄운다.
const failedPendingStores = new Set<string>();

/** 이 매장의 요청 번호를 브라우저 저장소에 남기지 못한 적이 있는가(그러면 새로고침 · 탭 닫기 뒤 잠금이 사라질 수 있다). */
export const pendingStorageFailed = (store: string | null): boolean => failedPendingStores.has(pendingKey(store));

/** 테스트용 — 메모리 대체 값과 실패 표시를 비운다. */
export function resetPendingMemory(): void {
  memoryPending.clear();
  failedPendingStores.clear();
}

function parsePendingValue(v: unknown): PendingSave | null {
  const p = v as Partial<PendingSave> | null;
  if (!p || typeof p.price !== 'number' || typeof p.requestId !== 'string') return null;
  return { price: p.price, requestId: p.requestId, at: typeof p.at === 'string' ? p.at : '' };
}

/** 옛 한 키(`[[id, {..}], ...]`) 보관 — 읽기만 하고, 다음 쓰기 때 카드별 키로 옮긴다. */
function readLegacyPending(prefix: string, out: Map<number, PendingSave>): boolean {
  const raw = localStorage.getItem(prefix);
  if (raw == null) return false;
  const parsed = JSON.parse(raw);
  if (!Array.isArray(parsed)) return true;
  for (const e of parsed) {
    if (!Array.isArray(e) || e.length < 2 || typeof e[0] !== 'number') continue;
    const v = parsePendingValue(e[1]);
    if (v) out.set(e[0], v);
  }
  return true;
}

export function loadPending(store: string | null): Map<number, PendingSave> {
  const prefix = pendingKey(store);
  const out = new Map<number, PendingSave>();
  try {
    try { readLegacyPending(prefix, out); } catch { /* 깨진 옛 값은 무시 */ }
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(`${prefix}:`)) continue;
      const id = Number(k.slice(prefix.length + 1));
      if (!Number.isInteger(id)) continue;
      try {
        const v = parsePendingValue(JSON.parse(localStorage.getItem(k) ?? 'null'));
        if (v) out.set(id, v);
      } catch {
        // 깨진 값은 무시
      }
    }
  } catch {
    // 저장소를 못 읽어도 아래 메모리 값은 쓴다.
  }
  memoryPending.forEach((v, k) => {
    if (!k.startsWith(`${prefix}:`)) return;
    const id = Number(k.slice(prefix.length + 1));
    if (v) out.set(id, v); else out.delete(id);
  });
  return out;
}

const samePending = (a: PendingSave | undefined, b: PendingSave | undefined): boolean =>
  !!a && !!b && a.price === b.price && a.requestId === b.requestId && a.at === b.at;

/**
 * 최신 값을 읽어 고친 뒤 **바뀐 카드 키만** 쓴다 — 다른 탭 · 겹친 저장의 다른 카드 기록을 덮지 않게. 쓰기가 실패하면(용량 초과 ·
 * 막힌 저장소) 그 값을 메모리에 남겨 이 탭의 잠금을 지키고 실패 표시를 켠다(pendingStorageFailed).
 */
export function updatePending(store: string | null, mutate: (m: Map<number, PendingSave>) => void): Map<number, PendingSave> {
  const prefix = pendingKey(store);
  const before = loadPending(store);
  const after = new Map(before);
  mutate(after);
  let legacy = false;
  try { legacy = localStorage.getItem(prefix) != null; } catch { legacy = false; }
  let ok = true;
  const write = (id: number, v: PendingSave | null) => {
    const k = pendingCardKey(store, id);
    try {
      if (v) localStorage.setItem(k, JSON.stringify(v)); else localStorage.removeItem(k);
      memoryPending.delete(k);
    } catch {
      memoryPending.set(k, v);
      ok = false;
    }
  };
  after.forEach((v, id) => { if (legacy || !samePending(before.get(id), v)) write(id, v); });
  before.forEach((_, id) => { if (!after.has(id)) write(id, null); });
  if (legacy && ok) {
    try { localStorage.removeItem(prefix); } catch { /* 다음에 다시 옮긴다 */ }
  }
  if (!ok) failedPendingStores.add(prefix);
  return after;
}

export function loadFavorites(store: string | null): Set<number> {
  try {
    const raw = localStorage.getItem(favoriteKey(store));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is number => typeof v === 'number') : []);
  } catch {
    return new Set();
  }
}

export function saveFavorites(store: string | null, set: Set<number>): void {
  try {
    localStorage.setItem(favoriteKey(store), JSON.stringify(Array.from(set)));
  } catch {
    // 저장할 수 없어도 이번 화면에서는 보인다.
  }
}

// ─── 일괄 적용 대상 ───────────────────────────────────────────────────────────

export interface BulkPlan {
  price:   number;
  targets: DecidedPriceRequestItem[];
  up:      number;
  down:    number;
  /** 네이버 가격이 없는(미조회) 카드 수 — 방향을 모른다. */
  noPrice: number;
  excluded: { pending: number; same: number; notBelow: number };
}

/**
 * 체크한 카드에 일괄 가격을 매긴다. 결과 불명 카드(같은 requestId 로만 다시 시도) · 이미 같은 가격은 빼고, "네이버 가격 미만만 적용" 이면
 * 네이버 가격이 일괄 가격보다 낮은 카드만(네이버 가격을 모르는 카드도 뺀다).
 */
export function planBulk(
  items: DecisionListItem[],
  selected: Set<number>,
  price: number,
  belowOnly: boolean,
  pending: Map<number, PendingSave>,
  makeId: () => string = newRequestId,
): BulkPlan {
  const plan: BulkPlan = { price, targets: [], up: 0, down: 0, noPrice: 0, excluded: { pending: 0, same: 0, notBelow: 0 } };
  for (const it of items) {
    if (!selected.has(it.cardId)) continue;
    if (pending.has(it.cardId)) { plan.excluded.pending += 1; continue; }
    const cur = it.currentPrice;
    if (belowOnly && (cur == null || cur >= price)) { plan.excluded.notBelow += 1; continue; }
    if (cur === price) { plan.excluded.same += 1; continue; }
    if (cur == null) plan.noPrice += 1;
    else if (price > cur) plan.up += 1;
    else plan.down += 1;
    plan.targets.push({ cardId: it.cardId, price, requestId: makeId() });
  }
  return plan;
}

export function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/**
 * 단건 판매가 저장(② 적용가 · 스토어 상품 관리)의 요청 번호(클라우드 리뷰 중간, 감사 card-controltower#651) — 판매가 결정 화면과 같은
 * 결과 불명 보관소(매장별)를 쓴다. 같은 카드 · 같은 가격의 보관된 요청이 있으면 그 번호를 다시 쓰고(백엔드의 같은 requestId 재전송
 * 보호 — 응답 시간 초과 뒤 다시 눌러도 네이버 PUT · PriceHub 보고가 한 번 더 나가지 않는다), 없으면 새로 만들어 보내기 전에 먼저 적는다.
 */
export function requestIdForSave(store: string | null, cardId: number, price: number): string {
  const prev = loadPending(store).get(cardId);
  if (prev && prev.price === price) return prev.requestId;
  const requestId = newRequestId();
  updatePending(store, (m) => { m.set(cardId, { price, requestId, at: new Date().toISOString() }); });
  return requestId;
}

/** 결과가 확실해졌으면(네이버 성공 · 실패 · 서버 거절) 보관을 지운다 — 그 사이 다른 요청으로 바뀌었으면 두고. */
export function settleSave(store: string | null, cardId: number, requestId: string): void {
  updatePending(store, (m) => { if (m.get(cardId)?.requestId === requestId) m.delete(cardId); });
}

/** 저장 결과로 보관을 지울지 — 결과 불명(UNKNOWN · 응답 없음 · 처리 중)만 남긴다. */
export const saveSettled = (naverStatus: string | null, errorKind?: SaveErrorOutcome['kind']): boolean =>
  errorKind != null ? errorKind === 'failed' : naverStatus === 'SUCCESS' || naverStatus === 'FAILED';
